import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router';
import { createAdminUser, getAdminUser, getApiErrorMessage, patchAdminUser } from '../lib/api';
import { CREATE_USER_TEMPLATE } from '../templates/createUserTemplate';

export default function CreateUserPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const template = useMemo(() => CREATE_USER_TEMPLATE, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const alertBox = root.querySelector<HTMLElement>('#create-user-alert');
    const alertIcon = root.querySelector<HTMLElement>('#create-user-alert-icon');
    const alertTitle = root.querySelector<HTMLElement>('#create-user-alert-title');
    const alertText = root.querySelector<HTMLElement>('#create-user-alert-text');
    const pageTitle = root.querySelector<HTMLElement>('#create-user-page-title');
    const breadcrumbLabel = root.querySelector<HTMLElement>('#create-user-breadcrumb-label');
    const submitLabel = root.querySelector<HTMLElement>('#create-user-submit-label');

    const form = root.querySelector<HTMLFormElement>('#create-user-form');
    const firstNameInput = root.querySelector<HTMLInputElement>('#user-first-name');
    const lastNameInput = root.querySelector<HTMLInputElement>('#user-last-name');
    const emailInput = root.querySelector<HTMLInputElement>('#user-email');
    const avatarInput = root.querySelector<HTMLInputElement>('#create-user-avatar-input');
    const avatarPreview = root.querySelector<HTMLImageElement>('#create-user-avatar-preview');
    const avatarPlaceholder = root.querySelector<HTMLElement>('#create-user-avatar-placeholder');
    const avatarTrigger = root.querySelector<HTMLButtonElement>('#create-user-avatar-trigger');
    const roleSelect = root.querySelector<HTMLSelectElement>('#user-role');
    const passwordInput = root.querySelector<HTMLInputElement>('#user-password');
    const confirmPasswordInput = root.querySelector<HTMLInputElement>('#user-password-confirm');
    const isActiveInput = root.querySelector<HTMLInputElement>('#user-is-active');
    const submitBtn = root.querySelector<HTMLButtonElement>('#create-user-submit');

    if (
      !alertBox ||
      !alertIcon ||
      !alertTitle ||
      !alertText ||
      !pageTitle ||
      !breadcrumbLabel ||
      !submitLabel ||
      !form ||
      !firstNameInput ||
      !lastNameInput ||
      !emailInput ||
      !avatarInput ||
      !avatarPreview ||
      !avatarPlaceholder ||
      !avatarTrigger ||
      !roleSelect ||
      !passwordInput ||
      !confirmPasswordInput ||
      !isActiveInput ||
      !submitBtn
    ) {
      return;
    }

    const editIdParam = searchParams.get('id');
    const editId = editIdParam ? Number(editIdParam) : NaN;
    const isEditMode = Number.isFinite(editId) && editId > 0;

    if (isEditMode) {
      pageTitle.textContent = 'Edit User';
      breadcrumbLabel.textContent = 'Edit User';
      submitLabel.textContent = 'Update User';
      passwordInput.required = false;
      confirmPasswordInput.required = false;
      passwordInput.placeholder = 'Leave blank to keep current password';
      confirmPasswordInput.placeholder = 'Leave blank to keep current password';
    }

    let disposed = false;
    let selectedAvatar: File | null = null;
    let avatarTouched = false;

    const showAlert = (kind: 'success' | 'error', title: string, text: string) => {
      alertBox.classList.remove('hidden', 'bg-emerald-50', 'border-emerald-200', 'bg-rose-50', 'border-rose-200');
      alertBox.classList.add('flex');

      if (kind === 'success') {
        alertBox.classList.add('bg-emerald-50', 'border-emerald-200');
        alertIcon.className = 'material-symbols-outlined text-emerald-600';
        alertIcon.textContent = 'check_circle';
        alertTitle.className = 'text-sm font-semibold text-emerald-800';
        alertText.className = 'text-sm text-emerald-700 opacity-80';
      } else {
        alertBox.classList.add('bg-rose-50', 'border-rose-200');
        alertIcon.className = 'material-symbols-outlined text-rose-600';
        alertIcon.textContent = 'error';
        alertTitle.className = 'text-sm font-semibold text-rose-800';
        alertText.className = 'text-sm text-rose-700 opacity-80';
      }

      alertTitle.textContent = title;
      alertText.textContent = text;
    };

    const setSaving = (saving: boolean) => {
      submitBtn.disabled = saving;
      submitBtn.classList.toggle('opacity-60', saving);
      submitBtn.classList.toggle('cursor-not-allowed', saving);
    };

    const setAvatarPreview = (url: string | null) => {
      if (!url) {
        avatarPreview.src = '';
        avatarPreview.classList.add('hidden');
        avatarPlaceholder.classList.remove('hidden');
        return;
      }
      avatarPreview.src = url;
      avatarPreview.classList.remove('hidden');
      avatarPlaceholder.classList.add('hidden');
    };

    const loadCurrentUser = async () => {
      if (!isEditMode) return;
      try {
        const current = await getAdminUser(editId);
        if (disposed) return;
        firstNameInput.value = current.first_name || '';
        lastNameInput.value = current.last_name || '';
        emailInput.value = current.email || '';
        roleSelect.value = current.role;
        isActiveInput.checked = current.is_active;
        if (current.avatar) setAvatarPreview(current.avatar);
      } catch (error) {
        if (disposed) return;
        showAlert('error', 'Load Failed', getApiErrorMessage(error, 'Failed to load user data.'));
      }
    };

    const onSubmit = async (event: SubmitEvent) => {
      event.preventDefault();

      const password = passwordInput.value;
      const confirmPassword = confirmPasswordInput.value;

      if ((password || confirmPassword) && password !== confirmPassword) {
        showAlert('error', 'Validation Error', 'Password and confirm password must match.');
        return;
      }

      if (!isEditMode && !password) {
        showAlert('error', 'Validation Error', 'Password is required for new user.');
        return;
      }

      setSaving(true);

      try {
        const basePayload = {
          first_name: firstNameInput.value.trim(),
          last_name: lastNameInput.value.trim(),
          email: emailInput.value.trim(),
          role: roleSelect.value as 'admin' | 'editor' | 'user',
          is_active: isActiveInput.checked,
        };

        if (isEditMode) {
          await patchAdminUser(editId, {
            ...basePayload,
            ...(password ? { password } : {}),
            ...(avatarTouched ? { avatar: selectedAvatar } : {}),
          });
        } else {
          await createAdminUser({
            ...basePayload,
            password: passwordInput.value,
            ...(selectedAvatar ? { avatar: selectedAvatar } : {}),
          });
        }

        if (disposed) return;
        showAlert('success', isEditMode ? 'User Updated' : 'User Created', isEditMode ? 'User updated successfully.' : 'New user account has been created successfully.');
        setTimeout(() => {
          if (!disposed) navigate('/users');
        }, 700);
      } catch (error) {
        if (disposed) return;
        showAlert('error', 'Request Failed', getApiErrorMessage(error, isEditMode ? 'Failed to update user.' : 'Failed to create user.'));
      } finally {
        if (!disposed) setSaving(false);
      }
    };

    const onAvatarTrigger = () => {
      avatarInput.click();
    };

    const onAvatarChange = () => {
      const file = avatarInput.files?.[0] ?? null;
      selectedAvatar = file;
      avatarTouched = true;
      if (!file) {
        setAvatarPreview(null);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (disposed) return;
        setAvatarPreview(typeof reader.result === 'string' ? reader.result : null);
      };
      reader.onerror = () => {
        if (disposed) return;
        setAvatarPreview(null);
      };
      reader.readAsDataURL(file);
    };

    form.addEventListener('submit', onSubmit);
    avatarTrigger.addEventListener('click', onAvatarTrigger);
    avatarInput.addEventListener('change', onAvatarChange);
    void loadCurrentUser();

    return () => {
      disposed = true;
      form.removeEventListener('submit', onSubmit);
      avatarTrigger.removeEventListener('click', onAvatarTrigger);
      avatarInput.removeEventListener('change', onAvatarChange);
    };
  }, [navigate, searchParams]);

  return (
    <>
      <Helmet>
        <title>MotoJourney | Create User</title>
      </Helmet>
      <div className="font-display bg-background-light text-slate-900 min-h-screen" ref={containerRef} dangerouslySetInnerHTML={{ __html: template }} />
    </>
  );
}
